import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Printer, ArrowLeft, Calendar, Download } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { format, parseISO, subDays, startOfWeek, endOfWeek } from "date-fns";
import PrintHeader from "@/components/PrintHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PrintReport() {
  const { type, date } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      if (type === "daily") {
        const res = await axios.get(`${API}/report/${date}`);
        setReport({ type: "daily", data: res.data });
      } else if (type === "weekly") {
        const dateObj = parseISO(date);
        const weekStart = format(startOfWeek(dateObj, { weekStartsOn: 1 }), "yyyy-MM-dd");
        const weekEnd = format(endOfWeek(dateObj, { weekStartsOn: 1 }), "yyyy-MM-dd");
        const res = await axios.get(`${API}/report/range/${weekStart}/${weekEnd}`);
        setReport({ type: "weekly", data: res.data });
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Error fetching report:", error);
    } finally {
      setLoading(false);
    }
  }, [type, date]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handlePrint = () => {
    window.print();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="p-8 text-center">
        <p className="text-muted-foreground">Report not found</p>
        <Button onClick={() => navigate(-1)} className="mt-4">Go Back</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Print Controls - Hidden when printing */}
      <div className="print:hidden glass-header px-4 md:px-8 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button onClick={handlePrint} className="btn-primary" data-testid="print-btn">
            <Printer className="w-4 h-4 mr-2" />
            Print Report
          </Button>
        </div>
      </div>

      {/* Report Content */}
      <div className="max-w-4xl mx-auto p-8 print:p-0">
        <div className="bg-white text-black p-8 rounded-lg print:rounded-none print:shadow-none shadow-xl" data-testid="print-report">
          <PrintHeader alwaysVisible={true} />
          {/* Report Header */}
          <div className="text-center border-b-2 border-black pb-6 mb-6">
            <h1 className="text-3xl font-bold font-mono">LOTTOOPS TERMINAL</h1>
            <p className="text-lg mt-2">
              {report.type === "daily" ? "DAILY REPORT" : "WEEKLY REPORT"}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              {report.type === "daily" 
                ? format(parseISO(report.data.date), "EEEE, MMMM d, yyyy")
                : `${format(parseISO(report.data.start_date), "MMM d")} - ${format(parseISO(report.data.end_date), "MMM d, yyyy")}`
              }
            </p>
          </div>

          {report.type === "daily" ? (
            /* Daily Report Content */
            <>
              {/* Denomination Table */}
              <table className="w-full border-collapse mb-6">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 p-2 text-left font-mono">Denom</th>
                    <th className="border border-gray-300 p-2 text-right font-mono">Opening</th>
                    <th className="border border-gray-300 p-2 text-right font-mono">Sales</th>
                    <th className="border border-gray-300 p-2 text-right font-mono">Activ.</th>
                    <th className="border border-gray-300 p-2 text-right font-mono">Deliv.</th>
                    <th className="border border-gray-300 p-2 text-right font-mono">Returns</th>
                    <th className="border border-gray-300 p-2 text-right font-mono">Closing</th>
                  </tr>
                </thead>
                <tbody>
                  {report.data.denominations?.map((d) => (
                    <tr key={d.denomination}>
                      <td className="border border-gray-300 p-2 font-mono font-bold">${d.denomination}</td>
                      <td className="border border-gray-300 p-2 text-right font-mono">{d.opening_stock}</td>
                      <td className="border border-gray-300 p-2 text-right font-mono">{d.sales}</td>
                      <td className="border border-gray-300 p-2 text-right font-mono">{d.activations}</td>
                      <td className="border border-gray-300 p-2 text-right font-mono">{d.deliveries}</td>
                      <td className="border border-gray-300 p-2 text-right font-mono">{d.returns}</td>
                      <td className="border border-gray-300 p-2 text-right font-mono font-bold">{d.closing_stock}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Totals */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-100 p-4 rounded text-center">
                  <p className="text-sm text-gray-600">TOTAL SALES</p>
                  <p className="text-2xl font-mono font-bold">{formatCurrency(report.data.total_sales_amount)}</p>
                </div>
                <div className="bg-gray-100 p-4 rounded text-center">
                  <p className="text-sm text-gray-600">TOTAL DELIVERIES</p>
                  <p className="text-2xl font-mono font-bold">{formatCurrency(report.data.total_deliveries_amount)}</p>
                </div>
                <div className="bg-gray-100 p-4 rounded text-center">
                  <p className="text-sm text-gray-600">TOTAL RETURNS</p>
                  <p className="text-2xl font-mono font-bold">{formatCurrency(report.data.total_returns_amount)}</p>
                </div>
              </div>

              {/* Footer */}
              <div className="border-t-2 border-black pt-4 text-sm text-gray-600">
                <p>Last Updated By: {report.data.last_updated_by || "System"}</p>
                <p>Report Generated: {format(new Date(), "MMM d, yyyy h:mm a")}</p>
              </div>
            </>
          ) : (
            /* Weekly Report Content */
            <>
              {/* Summary Totals */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-gray-100 p-4 rounded text-center">
                  <p className="text-sm text-gray-600">TOTAL SALES</p>
                  <p className="text-xl font-mono font-bold">{formatCurrency(report.data.totals?.total_sales)}</p>
                </div>
                <div className="bg-gray-100 p-4 rounded text-center">
                  <p className="text-sm text-gray-600">DELIVERIES</p>
                  <p className="text-xl font-mono font-bold">{formatCurrency(report.data.totals?.total_deliveries)}</p>
                </div>
                <div className="bg-gray-100 p-4 rounded text-center">
                  <p className="text-sm text-gray-600">ACTIVATIONS</p>
                  <p className="text-xl font-mono font-bold">{formatCurrency(report.data.totals?.total_activations)}</p>
                </div>
                <div className="bg-gray-100 p-4 rounded text-center">
                  <p className="text-sm text-gray-600">RETURNS</p>
                  <p className="text-xl font-mono font-bold">{formatCurrency(report.data.totals?.total_returns)}</p>
                </div>
              </div>

              {/* By Denomination */}
              <h3 className="font-mono font-bold mb-3">BY DENOMINATION</h3>
              <table className="w-full border-collapse mb-6">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 p-2 text-left font-mono">Denom</th>
                    <th className="border border-gray-300 p-2 text-right font-mono">Sales (Qty)</th>
                    <th className="border border-gray-300 p-2 text-right font-mono">Sales ($)</th>
                    <th className="border border-gray-300 p-2 text-right font-mono">Deliveries</th>
                    <th className="border border-gray-300 p-2 text-right font-mono">Activations</th>
                    <th className="border border-gray-300 p-2 text-right font-mono">Returns</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(report.data.totals?.by_denomination || {}).map(([denom, data]) => (
                    <tr key={denom}>
                      <td className="border border-gray-300 p-2 font-mono font-bold">${denom}</td>
                      <td className="border border-gray-300 p-2 text-right font-mono">{data.sales}</td>
                      <td className="border border-gray-300 p-2 text-right font-mono">{formatCurrency(data.sales * parseInt(denom))}</td>
                      <td className="border border-gray-300 p-2 text-right font-mono">{data.deliveries}</td>
                      <td className="border border-gray-300 p-2 text-right font-mono">{data.activations}</td>
                      <td className="border border-gray-300 p-2 text-right font-mono">{data.returns}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Daily Breakdown */}
              <h3 className="font-mono font-bold mb-3">DAILY BREAKDOWN</h3>
              <table className="w-full border-collapse mb-6">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 p-2 text-left font-mono">Date</th>
                    <th className="border border-gray-300 p-2 text-right font-mono">Sales</th>
                    <th className="border border-gray-300 p-2 text-right font-mono">Deliveries</th>
                    <th className="border border-gray-300 p-2 text-right font-mono">Returns</th>
                  </tr>
                </thead>
                <tbody>
                  {report.data.entries?.map((entry) => (
                    <tr key={entry.date}>
                      <td className="border border-gray-300 p-2 font-mono">{format(parseISO(entry.date), "EEE, MMM d")}</td>
                      <td className="border border-gray-300 p-2 text-right font-mono">{formatCurrency(entry.total_sales_amount)}</td>
                      <td className="border border-gray-300 p-2 text-right font-mono">{formatCurrency(entry.total_deliveries_amount)}</td>
                      <td className="border border-gray-300 p-2 text-right font-mono">{formatCurrency(entry.total_returns_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Footer */}
              <div className="border-t-2 border-black pt-4 text-sm text-gray-600">
                <p>Entries Count: {report.data.entry_count}</p>
                <p>Report Generated: {format(new Date(), "MMM d, yyyy h:mm a")}</p>
              </div>
            </>
          )}

          {/* Signature Line */}
          <div className="mt-12 pt-8 border-t border-gray-300 grid grid-cols-2 gap-8">
            <div>
              <div className="border-b border-black w-full mb-2"></div>
              <p className="text-sm text-gray-600">Manager Signature</p>
            </div>
            <div>
              <div className="border-b border-black w-full mb-2"></div>
              <p className="text-sm text-gray-600">Date</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
